import { useState } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
  RegisterOptions,
} from 'react-hook-form';
import { Form } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  DarwinInput,
  DarwinSelect,
  DarwinTreeSelect,
  DarwinSwitch,
  DarwinDatePicker,
  DarwinTooltip,
  DarwinButton,
  DarwinSpin,
  DarwinCheckbox,
  DarwinDivider,
} from 'components/darwin-ui';
import { Input } from 'antd';
import { InputType } from 'types/genericTypes';
import { TreeNode } from 'types/productsTypes';
import { SectionFieldEditModal } from './dataCapture/SectionFieldEditModal';
import styles from './Ui.module.css';
import { DarwinAutoComplete } from 'components/darwin-ui/AutoComplete';
import FileImageUploader from './organizations/NewOrganization/UploaderOrganization';
import { EudrRegulationDocument } from './organizations/NewOrganization/EudrRegulationSection';

const { TextArea } = Input;
const { Option } = DarwinSelect;

const DATE_PICKER_STYLES = {
  width: '100%',
};

interface DropdownItem {
  id: string;
  label: string;
  phoneSelectKey?: boolean;
}

export interface GenericInputProps<TFormValues extends FieldValues> {
  control: Control<TFormValues>;
  errors: FieldErrors<TFormValues>;
  inputType: InputType;
  fieldName: Path<TFormValues>;
  label: string;
  showRequiredAsterisk?: boolean;
  inputRules: RegisterOptions<TFormValues>;
  maxLength?: number;
  disabled?: boolean;
  dropdownData?: DropdownItem[];
  dropdownTreeData?: TreeNode[];
  tooltipIcon?: React.ReactNode;
  tooltipText?: string;
  showCount?: boolean;
  isDropdownMultiple?: boolean;
  enableSearchInDropdown?: boolean;
  displayDataCaptureEditButton?: boolean;
  treeCheckable?: boolean;
  treeNodeFilterProp?: string;
  filteredOptions?: { label: string; value: string; email: string }[];
  onAutoComplete?: (
    email: string,
    contactPhone: { phoneCode: string; phoneNumber: string }
  ) => void;
  onCheckEndOrInitialFlow?: (fieldName: string, value: boolean) => void;
  onSelectCountry?: (phoneCode: string) => void;
  autoCompleting?: boolean;
  autoCompletingPhoneCode?: boolean;
  defaultFieldName?: string;
  defaultHelpText?: string;
  originalFieldName?: string;
  originalHelpText?: string;
  isVisible?: boolean;
  checkboxText?: string;
  isCheckboxChecked?: boolean;
  isCreateForm?: boolean;
  documentHandler?: (id: string, type: EudrRegulationDocument) => void;
  type?: EudrRegulationDocument;
  removeDocumentHandler?: (id: string) => void;
  listedDocuments?: string[];
  folder?: string;
}

export const GenericInput = <TFormValues extends FieldValues>({
  control,
  errors,
  inputType,
  fieldName,
  label,
  showRequiredAsterisk,
  inputRules,
  maxLength,
  disabled,
  dropdownData,
  dropdownTreeData,
  tooltipIcon,
  tooltipText,
  showCount,
  isDropdownMultiple = false,
  enableSearchInDropdown = false,
  displayDataCaptureEditButton = false,
  treeCheckable = false,
  treeNodeFilterProp,
  filteredOptions,
  onAutoComplete,
  onCheckEndOrInitialFlow,
  onSelectCountry,
  autoCompleting,
  autoCompletingPhoneCode,
  defaultFieldName,
  defaultHelpText,
  originalFieldName,
  originalHelpText,
  isVisible,
  checkboxText,
  isCheckboxChecked,
  isCreateForm = false,
  documentHandler,
  removeDocumentHandler,
  type,
  listedDocuments,
  folder,
}: GenericInputProps<TFormValues>): JSX.Element => {
  const { t } = useTranslation();
  // data capture edit field modal logic
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };
  const handleCloseModal = () => setIsModalOpen(false);

  return (
    <>
      <Form.Item
        style={{ display: isVisible === false ? 'none' : undefined }} // Hide the input if isVisible is false
        label={
          <>
            {label}
            {showRequiredAsterisk && (
              <span className={styles.formRequiredAsterisk}>*</span>
            )}
            {tooltipText && tooltipIcon && (
              <DarwinTooltip title={tooltipText}>
                <span className={styles.tooltip}>{tooltipIcon}</span>
              </DarwinTooltip>
            )}
            {displayDataCaptureEditButton && (
              <DarwinButton
                type="link"
                className={styles.dataCaptureEditButton}
                onClick={handleOpenModal}
              >
                {t('edit')}
              </DarwinButton>
            )}
          </>
        }
        validateStatus={errors[fieldName] ? 'error' : ''}
        help={
          errors[fieldName]?.message
            ? String(errors[fieldName]?.message)
            : undefined
        }
        className={styles.formInputTitle}
      >
        <Controller
          name={fieldName}
          control={control}
          disabled={disabled}
          rules={inputRules}
          render={({ field }) => {
            if (inputType === InputType.TEXTAREA) {
              return (
                <TextArea
                  showCount={showCount}
                  maxLength={maxLength}
                  {...field}
                  rows={4}
                  onChange={e => {
                    const value = e.target.value;
                    if (!!maxLength && value.length <= maxLength) {
                      field.onChange(value);
                    }
                  }}
                />
              );
            }

            if (inputType === InputType.SWITCH) {
              return <DarwinSwitch {...field} />;
            }

            if (inputType === InputType.UPLOAD) {
              return (
                <>
                  <FileImageUploader
                    documentHandler={documentHandler ?? (() => {})}
                    removeDocumentHandler={removeDocumentHandler ?? (() => {})}
                    type={type}
                    listedDocuments={listedDocuments}
                    folder={folder}
                  />
                  <DarwinDivider />
                </>
              );
            }

            if (inputType === InputType.CHECKBOX) {
              return (
                <DarwinCheckbox
                  disabled={disabled}
                  {...field}
                  defaultChecked={isCheckboxChecked}
                  onChange={e => {
                    field.onChange(e.target.checked);
                    onCheckEndOrInitialFlow?.(fieldName, e.target.checked);
                  }}
                >
                  {checkboxText || ''}
                </DarwinCheckbox>
              );
            }

            if (inputType === InputType.TREESELECT) {
              return (
                <DarwinTreeSelect
                  {...field}
                  className={`${styles.productSelector} ${
                    field.value?.length ? styles.expanded : ''
                  }`}
                  treeData={dropdownTreeData}
                  treeNodeFilterProp={
                    enableSearchInDropdown ? 'title' : undefined
                  }
                  showSearch={enableSearchInDropdown ? true : false}
                  filterTreeNode={
                    enableSearchInDropdown
                      ? (input, treeNode) =>
                          String(treeNode?.title ?? '')
                            .toLowerCase()
                            .includes(input.toLowerCase())
                      : undefined
                  }
                  treeCheckable={treeCheckable}
                />
              );
            }

            if (inputType === InputType.AUTOCOMPLETE) {
              return (
                <DarwinAutoComplete
                  {...field}
                  options={filteredOptions}
                  onSelect={(value, option) => {
                    const contactPhone = {
                      phoneCode: option.phoneCode,
                      phoneNumber: option.phoneNumber,
                    };
                    onAutoComplete?.(option.email, contactPhone);
                  }}
                  notFoundContent={
                    autoCompleting ? (
                      <DarwinSpin spinning />
                    ) : (
                      t(
                        'create-organization-form.data-sharing-modal.contactNotFound'
                      )
                    )
                  }
                />
              );
            }

            if (inputType === InputType.DATEPICKER) {
              return (
                <DarwinDatePicker
                  {...field}
                  style={DATE_PICKER_STYLES}
                  disabled
                  placeholder=""
                  onChange={date => field.onChange(date)}
                  value={field.value ? field.value : null}
                  format="yyyy-MM-dd HH:mm"
                  showTime
                />
              );
            }

            if (inputType === InputType.DROPDOWN) {
              return (
                <DarwinSelect
                  {...field}
                  optionFilterProp={
                    enableSearchInDropdown ? 'label' : undefined
                  }
                  showSearch={enableSearchInDropdown ? true : false}
                  filterOption={
                    enableSearchInDropdown
                      ? (input, option) =>
                          String(option?.label ?? '')
                            .toLowerCase()
                            .includes(input.toLowerCase())
                      : undefined
                  }
                  onSelect={value => {
                    onSelectCountry?.(value);
                  }}
                  mode={isDropdownMultiple ? 'multiple' : undefined}
                >
                  {dropdownData?.map(item => (
                    <Option
                      key={
                        item.phoneSelectKey
                          ? `${item.id} - ${item.label}`
                          : item.id
                      }
                      value={item.id}
                      label={item.label}
                    >
                      {item.label}
                    </Option>
                  ))}
                </DarwinSelect>
              );
            }

            if (inputType === InputType.PHONE) {
              return (
                <Controller
                  name={fieldName}
                  control={control}
                  rules={{
                    required: t('common.required-phone-number'),
                    validate: {
                      phoneNumberRequired: value =>
                        value?.phoneNumber
                          ? true
                          : t('common.required-phone-number'),

                      phoneNumberFormat: value =>
                        /^\d{7,10}$/.test(value?.phoneNumber || '')
                          ? true
                          : t('common.invalid-phone-number'),
                    },
                  }}
                  render={({ field }) => (
                    <DarwinSpin spinning={autoCompleting ?? false}>
                      <DarwinInput
                        prefix={
                          field.value?.phoneCode ? (
                            `+${field.value?.phoneCode}`
                          ) : (
                            <span />
                          )
                        }
                        disabled={disabled}
                        name={`${fieldName}.phoneNumber`}
                        value={field.value?.phoneNumber || ''}
                        onChange={e => {
                          field.onChange({
                            ...field.value,
                            phoneNumber: e.target.value,
                          });
                        }}
                        maxLength={10}
                      />
                    </DarwinSpin>
                  )}
                />
              );
            }

            return (
              <DarwinSpin spinning={autoCompleting ?? false}>
                <DarwinInput
                  {...field}
                  onChange={e => {
                    const value = e.target.value;
                    if (!!maxLength && value.length <= maxLength) {
                      field.onChange(value, { shouldDirty: true });
                      return;
                    }
                    if (!maxLength) {
                      field.onChange(value, { shouldDirty: true });
                      return;
                    }
                  }}
                />
              </DarwinSpin>
            );
          }}
        />
      </Form.Item>

      {/* data capture modal to edit field_name and help_text */}
      {displayDataCaptureEditButton && (
        <SectionFieldEditModal
          fieldName={fieldName}
          handleCloseModal={handleCloseModal}
          isModalOpen={isModalOpen}
          defaultFieldName={defaultFieldName}
          defaultHelpText={defaultHelpText}
          originalFieldName={originalFieldName}
          originalHelpText={originalHelpText}
        />
      )}
    </>
  );
};
